import type { JSX } from 'react';
import { BubbleMenu, type Editor } from '@tiptap/react';
import {
  BoldOutlined,
  CodeOutlined,
  HighlightOutlined,
  ItalicOutlined,
  LinkOutlined,
  StrikethroughOutlined,
  UnderlineOutlined,
} from '@ant-design/icons';

import { CALLOUT_TYPES } from './callout-extension';

/**
 * Bubble-меню — всплывает при выделении текста (как в Notion / Teamly).
 * Содержит самое нужное: B / I / U / S / code / highlight / link.
 *
 * Активные кнопки подсвечены оранжевым.
 */
export function TiptapBubbleMenu({ editor }: { editor: Editor }): JSX.Element {
  return (
    <BubbleMenu
      editor={editor}
      pluginKey="textBubble"
      tippyOptions={{ duration: 100, placement: 'top' }}
      shouldShow={({ editor, from, to }) => {
        // Прячем bubble в code-block (там не нужно форматирование)
        if (editor.isActive('codeBlock')) return false;
        // Прячем при выборе блок-ноды (картинка/медиа) — у них своя панель,
        // иначе текстовое меню перекрывает их тулбар.
        if (editor.isActive('image') || editor.isActive('mediaEmbed')) return false;
        const sel = editor.state.selection as unknown as { node?: unknown };
        if (sel.node) return false; // NodeSelection (atom) — не текст
        return from !== to;
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 2,
          background: '#1f1f1f',
          color: '#fff',
          borderRadius: 8,
          padding: 4,
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        }}
      >
        <BBtn
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <BoldOutlined />
        </BBtn>
        <BBtn
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <ItalicOutlined />
        </BBtn>
        <BBtn
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineOutlined />
        </BBtn>
        <BBtn
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <StrikethroughOutlined />
        </BBtn>
        <BBtn
          active={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}
        >
          <CodeOutlined />
        </BBtn>
        <BBtn
          active={editor.isActive('highlight')}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
        >
          <HighlightOutlined />
        </BBtn>
        <BBtn
          active={editor.isActive('link')}
          onClick={() => {
            const previousUrl = editor.getAttributes('link').href;
            const url = window.prompt('URL ссылки', previousUrl || 'https://');
            if (url === null) return;
            if (url === '') {
              editor.chain().focus().unsetLink().run();
              return;
            }
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
          }}
        >
          <LinkOutlined />
        </BBtn>
      </div>
    </BubbleMenu>
  );
}

/**
 * Тулбар «настройки блока» для callout — всплывает, когда курсор внутри
 * выделенного блока. 7 видов (эмодзи + цвет): клик меняет severity ноды.
 * Round-trip целый — severity сериализуется в маркер `> [!X]`.
 */
export function CalloutBubbleMenu({ editor }: { editor: Editor }): JSX.Element {
  const current = CALLOUT_TYPES.find((t) => editor.isActive('callout', { severity: t.code }));
  return (
    <BubbleMenu
      editor={editor}
      pluginKey="calloutBubble"
      tippyOptions={{ duration: 100, placement: 'top' }}
      shouldShow={({ editor }) => editor.isActive('callout')}
    >
      <div
        style={{
          display: 'flex',
          gap: 2,
          background: '#fff',
          border: '1px solid var(--fk-border, #e6e6ea)',
          borderRadius: 10,
          padding: 5,
          boxShadow: '0 8px 28px rgba(37,29,74,0.16)',
        }}
      >
        {CALLOUT_TYPES.map((t) => {
          const active = current?.code === t.code;
          return (
            <button
              key={t.code}
              type="button"
              title={t.label}
              onClick={() => editor.chain().focus().updateAttributes('callout', { severity: t.code }).run()}
              style={{
                background: active ? `${t.color}1A` : 'transparent',
                border: active ? `1.5px solid ${t.color}` : '1.5px solid transparent',
                cursor: 'pointer',
                padding: '4px 7px',
                borderRadius: 7,
                fontSize: 16,
                lineHeight: 1,
                display: 'inline-flex',
                alignItems: 'center',
                transition: 'background .1s',
              }}
              onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLButtonElement).style.background = '#f3f3f6'; }}
              onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              {t.emoji}
            </button>
          );
        })}
      </div>
    </BubbleMenu>
  );
}

function BBtn({
  active,
  onClick,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? 'var(--brasol-accent)' : 'transparent',
        color: '#fff',
        border: 'none',
        cursor: 'pointer',
        padding: '4px 8px',
        borderRadius: 4,
        fontSize: 14,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 26,
      }}
    >
      {children}
    </button>
  );
}
